import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ContactTypes, messages, type ContactType } from '@nesc/shared';
import { api, errorMessage } from '@/lib/api-client';
import { qk } from '@/lib/query-client';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field } from '@/components/ui/field';
import { Input, Select } from '@/components/ui/input';

export const ContactDialog = ({
  open,
  onOpenChange,
  companyId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  companyId: string;
}) => {
  const queryClient = useQueryClient();
  const [type, setType] = useState<ContactType>('email');
  const [value, setValue] = useState('');
  const [label, setLabel] = useState('');
  const [isPrimary, setIsPrimary] = useState(false);

  const mutation = useMutation({
    mutationFn: () =>
      api.post(`/companies/${companyId}/contacts`, {
        type,
        value,
        label: label || undefined,
        isPrimary,
      }),
    onSuccess: () => {
      toast.success('連絡先を追加しました');
      void queryClient.invalidateQueries({ queryKey: qk.company(companyId) });
      onOpenChange(false);
      setValue('');
      setLabel('');
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>連絡先を追加する</DialogTitle>
          <DialogDescription>
            手入力した連絡先は確度「高」として記録されます
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="space-y-3">
          <Field label="種別" required>
            {(props) => (
              <Select {...props} value={type} onChange={(e) => setType(e.target.value as ContactType)}>
                {ContactTypes.list.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label="値" required>
            {(props) => (
              <Input
                {...props}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                inputMode={type === 'phone' || type === 'fax' ? 'tel' : type === 'email' ? 'email' : 'url'}
                placeholder={
                  type === 'email'
                    ? 'info@example.co.jp'
                    : type === 'phone'
                      ? '03-1234-5678'
                      : type === 'form'
                        ? 'https://example.co.jp/contact'
                        : ''
                }
              />
            )}
          </Field>
          <Field label="ラベル" hint="「代表」「問い合わせ窓口」など">
            {(props) => <Input {...props} value={label} onChange={(e) => setLabel(e.target.value)} />}
          </Field>
          <label className="flex min-h-touch items-center gap-2 text-body">
            <input
              type="checkbox"
              className="size-4 accent-foreground"
              checked={isPrimary}
              onChange={(e) => setIsPrimary(e.target.checked)}
            />
            この種別の主連絡先にする
          </label>
        </DialogBody>

        <DialogFooter>
          <Button variant="secondary" size="lg" onClick={() => onOpenChange(false)}>
            {messages.common.cancel}
          </Button>
          <Button size="lg" onClick={() => mutation.mutate()} disabled={mutation.isPending || !value.trim()}>
            {messages.common.add}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
